import { AppHubCard } from "@/components/common/AppHubCard";
import { AppHubPage } from "@/components/common/AppHubPage";
import { useNav } from "@/utils/nav";
import { createFileRoute } from "@tanstack/react-router";
import { AUTH_SCREEN_GROUPS, authScreenGroupSlug } from "@/features/auth/screens/AuthScreensGallery";
import { ClipboardCheck, FileText, FormInput, KeyRound, Shapes } from "lucide-react";

const designItems = [
    {
        title: "Components",
        description: "Preview shared form fields, search, select dialogs, buttons and dictionaries.",
        icon: <FormInput className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-violet-500 to-violet-600",
        bgGradient: "bg-gradient-to-br from-violet-50 to-violet-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-violet-100/50 before:to-violet-200/50",
        borderColor: "border-violet-200 hover:border-violet-300",
        primaryButtonClassName: "bg-violet-600 hover:bg-violet-700",
        links: [
            { to: "/design/components/buttons", label: "Buttons" },
            { to: "/design/components/inputs", label: "Form fields" },
            { to: "/design/components/search-fields", label: "Search" },
            { to: "/design/components/select-dialogs", label: "Select dialogs" },
            { to: "/design/components/dictionaries", label: "Dictionaries" },
        ],
    },
    {
        title: "Auth",
        description:
            "Every sign-in, sign-up and account screen, grouped by step. Open a group, then step through its screens with Back and Next.",
        icon: <KeyRound className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-emerald-500 to-emerald-600",
        bgGradient: "bg-gradient-to-br from-emerald-50 to-emerald-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-emerald-100/50 before:to-emerald-200/50",
        borderColor: "border-emerald-200 hover:border-emerald-300",
        primaryButtonClassName: "bg-emerald-600 hover:bg-emerald-700",
        links: AUTH_SCREEN_GROUPS.map((group) => ({
            to: "/design/components/auth",
            label: group,
            search: { group: authScreenGroupSlug(group) },
        })),
    },
    {
        title: "Forms",
        description:
            "What patients see on a form. Try the form settings, such as side and title alignment, on a live cover and question page, then see annotated pages, every input type and every block.",
        icon: <FileText className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-sky-500 to-sky-600",
        bgGradient: "bg-gradient-to-br from-sky-50 to-sky-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-sky-100/50 before:to-sky-200/50",
        borderColor: "border-sky-200 hover:border-sky-300",
        primaryButtonClassName: "bg-sky-600 hover:bg-sky-700",
        links: [
            { to: "/design/forms", label: "Form settings" },
            { to: "/design/forms/pages", label: "Pages" },
            { to: "/design/forms/inputs", label: "Inputs" },
            { to: "/design/forms/blocks", label: "Blocks" },
            { to: "/design/forms/entry-values", label: "Entry values" },
        ],
    },
    {
        title: "Icons",
        description: "Animated Lottie icons, diary state illustrations, and rating or pain faces.",
        icon: <Shapes className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-fuchsia-500 to-fuchsia-600",
        bgGradient: "bg-gradient-to-br from-fuchsia-50 to-fuchsia-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-fuchsia-100/50 before:to-fuchsia-200/50",
        borderColor: "border-fuchsia-200 hover:border-fuchsia-300",
        primaryButtonClassName: "bg-fuchsia-600 hover:bg-fuchsia-700",
        links: [
            { to: "/design/icons/animations", label: "Animations" },
            { to: "/design/icons/states", label: "States" },
            { to: "/design/icons/faces", label: "Faces" },
        ],
    },
    {
        title: "Features",
        description:
            "Preview product flows, including multi-step onboarding, study clinician invites, worklist automations, build custom reminder messages, and inspect the schedule used to send reminders and other messages.",
        icon: <ClipboardCheck className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-indigo-500 to-indigo-600",
        bgGradient: "bg-gradient-to-br from-indigo-50 to-indigo-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-indigo-100/50 before:to-indigo-200/50",
        borderColor: "border-indigo-200 hover:border-indigo-300",
        primaryButtonClassName: "bg-indigo-600 hover:bg-indigo-700",
        links: [
            { to: "/design/features/otivio-frictionless-onboarding", label: "Multi-step onboarding" },
            { to: "/design/features/manager-study-clinician-invite-plan", label: "Study clinician invites" },
            { to: "/design/features/worklist", label: "Worklist" },
            { to: "/design/features/worklist/automations", label: "Automations" },
            { to: "/design/features/reminder-sequences", label: "Reminder sequences" },
            { to: "/design/features/messaging-schedule", label: "Messaging schedule" },
        ],
    },
];

const DesignPage = () => {
    useNav([{ label: "Design", route: { to: "/design" } }]);

    return (
        <AppHubPage
            title="Design"
            description="Component gallery and product-flow previews for building Patient Watch."
        >
            {designItems.map((item) => (
                <AppHubCard key={item.title} {...item} />
            ))}
        </AppHubPage>
    );
};

export const Route = createFileRoute("/_app/design/")({
    component: DesignPage,
});
